import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import featureFlagService from "services/FeatureFlagService";
import { defaultHarnessKeyFromLegacyKey } from '@pearsontechnology/bronte-lib-harness';

/**
 * Provides a prop getFeatureFlagValue, returns the latest value of given featureFlag.
 *  
 * sets the initial values of all flags using featureFlagService.getReady().
 * updates values of received flagNames using featureFlagService.subscribe() 
 * 
 * @param {ReactComponent} Component The component to wrap
 * @param {Array} flagNames flagNames being consumed by the component
 * @param {Boolean} shouldRenderBeforeFFavailable whether the component should render w/o FF being available
 */

export default function enhanceWithFeatureFlagSubscription(Component, flagNames, shouldRenderBeforeFFavailable = false) {
  return (props) => {
    if (!(flagNames && isArray(flagNames) && flagNames.length))
      throw new Error(
        `Problem: you consumed hoc 'enhanceWithFeatureFlagSubscription' for ${Component} 
        without providing the flagNames. 
        Pls provide the flagNames or remove the hoc`
      );
    
    const [featureFlags, setFeatureFlags] = useState({});
    const [areFeatureFlagsAvailable, setAreFeatureFlagsAvailable] = useState(false);

    const updateFeatureFlag = (flag) => {
      setFeatureFlags((prev) => ({
        ...prev,
        [flag.flagName]: flag.value,
      }));
    };

    const getFeatureFlagValue = (flagName) => {
      {
        if (!areFeatureFlagsAvailable) {
          throw new Error(
            `Problem: you asked to render without waiting (via shouldRenderBeforeFFavailable) 
            and tried to check a feature flag ${flagName} before the feature flags were retrieved from the server. 
            Solution: wait for areFeatureFlagsAvailable prop to be true before attempting
            to check these flags providedNames: ${flagNames}`
          );
        }
        const resolvedFlagName = defaultHarnessKeyFromLegacyKey(flagName);
        if (!featureFlags.hasOwnProperty(resolvedFlagName)) {
          /*eslint-disable-next-line no-console*/
          console.warn(
            `[FeatureFlags] flag_not_in_map (flagName=${flagName}, resolvedFlagName=${resolvedFlagName}, providedNames=${String(
              flagNames
            )}) enhanceWithFeatureFlagSubscription`
          );
          return false;
        }
        return featureFlags[resolvedFlagName];
      }
      
    };

    async function initFlags() {
      //Waits and sets the initial values of all the featureFlags
      const featureFlagValues = await featureFlagService.getWhenReady();
      setFeatureFlags(featureFlagValues);
      setAreFeatureFlagsAvailable(true);
    }

    useEffect(() => {
      initFlags();
    }, []);

    //method for setting the latest values of given flagNames
    useEffect(function subscribeToFeatureFlags() {
      if (areFeatureFlagsAvailable) {
        flagNames.forEach((flag) => {
          featureFlagService.subscribe(flag, updateFeatureFlag);
        });
      }
      return function unsubscribeFromFeatureFlags() {
        {
          flagNames.forEach((flag) => {
            featureFlagService.unsubscribe(flag);
          });
        }
      };
    }, [areFeatureFlagsAvailable]);

    return (
      (shouldRenderBeforeFFavailable || areFeatureFlagsAvailable) && (
        <Component
          {...props}
          getFeatureFlagValue={getFeatureFlagValue}
          areFeatureFlagsAvailable={
            shouldRenderBeforeFFavailable ? areFeatureFlagsAvailable : undefined
          }
        />
      )
    );
  };
}